import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { EquipmentItem } from '@/backend/models/equipmentItem';
import { I18n } from '@/i18n/I18n';
import { EquipmentConnectionState } from '@/frontend/hooks/useEquipmentConnection';
import { useConnectionConfig } from '@/frontend/hooks/useConnectionConfig';
import { DESKTOP_INSTRUMENT_TYPE, DesktopInstrument } from '@/types/instrumentBridgeTypes';
import { CameraConnectionConfig } from './CameraConnectionConfig';
import { ConnectionConfigFields } from './ConnectionConfigFields';

/**
 * The connect dialog, opened by choosing an instrument.
 *
 * Ported from veriform-desktop's `ConnectionContent` (Equipment/): titled with
 * the equipment item, the provider's fields in the body, and two actions --
 * Confirm records the choice, Confirm and connect records it and starts the
 * provider. The second is hidden for the types that have nothing to start.
 *
 * What differs is underneath. Desktop writes its own tables and calls its own
 * `setState`; here both actions are one bridge call that differs only in
 * whether it is told to start.
 *
 * A camera takes a different shape, as it does there: no provider schema, just
 * which device to point at.
 */

/** ConnectionContent.tsx:107 -- neither has a provider to start. */
function canConnect(instrument: DesktopInstrument): boolean {
  return instrument.instrumentType !== DESKTOP_INSTRUMENT_TYPE.ITEM
    && instrument.instrumentType !== DESKTOP_INSTRUMENT_TYPE.MANUAL_CONTROL;
}

export function ConnectionConfigModal(props: {
  equipment: EquipmentItem;
  connection: EquipmentConnectionState;
  instrument: DesktopInstrument;
  onClose: () => void;
}) {
  const { equipment, connection, instrument, onClose } = props;

  const isCamera = instrument.instrumentType === DESKTOP_INSTRUMENT_TYPE.CAMERA;
  // A camera has no provider schema to ask for.
  const config = useConnectionConfig(equipment, isCamera ? undefined : instrument, connection.storedConfiguration);
  const [cameraConfig, setCameraConfig] = useState<string>();

  const configuration = () => (isCamera ? cameraConfig ?? '{}' : JSON.stringify(config.fields));

  const confirm = (start: boolean) => {
    onClose();
    if (start) connection.startWith(instrument.id, configuration());
    else connection.saveWith(instrument.id, configuration());
  };

  /**
   * A provider with nothing to configure never shows an empty dialog: the
   * choice is recorded and it closes, which is what ConnectionContent.tsx:97
   * does when the schema comes back with no fields. Manual control is the
   * exception there too -- it stays open for an explicit Confirm.
   */
  const isManual = instrument.instrumentType === DESKTOP_INSTRUMENT_TYPE.MANUAL_CONTROL;
  useEffect(() => {
    if (isCamera || isManual || config.loading || !config.empty) return;
    confirm(false);
    // Only once the schema has come back empty.
  }, [isCamera, isManual, config.loading, config.empty]);

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{equipment.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        { isCamera
          ? <CameraConnectionConfig
              storedConfiguration={connection.storedConfiguration}
              setConfig={setCameraConfig}
            />
          : <ConnectionConfigFields config={config} /> }
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-primary" onClick={() => confirm(false)}>
          {I18n.get('confirm')}
        </button>
        { canConnect(instrument) && (
          <button type="button" className="btn btn-primary" onClick={() => confirm(true)}>
            {I18n.get('confirmAndConnect')}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
